import React from 'react'
import { useHistory } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import Datalist from 'components/Datalist'
import { Box } from 'components/Layout'
import {
  HistoryTests,
  HistoryTestsVariables,
  SortDirectionEnum,
  getFormattedDateAndTime,
  useAuth,
  UserRole,
  HistoryTests_tests_tests,
  HistoryTests_tests_tests_Members,
  useSubperson,
  useUser,
} from '@modmd/data'
import { ROUTES } from 'internal-portal/constants/routes'
import Card from 'components/Card'
import { Dropdown } from 'components/AutoCompleteDropdown'
import { DEVICE, RADIUS } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Label from 'components/Label'
import { isInfluenza } from 'utils/helpers'
import { USER_TEST_HISTORY } from './operations'

export interface Params {
  userId: string
}

const TEST_SESSIONS_PAGE_LENGTH = 10
const SKELETON_COLUMNS = 4

export const TestsUserDetails: React.VFC<{ userId: string }> = ({ userId }) => {
  const history = useHistory()
  const { hasRoles } = useAuth()
  const isSuperAdminOrResults = hasRoles([UserRole.SUPER_ADMIN, UserRole.RESULTS, UserRole.CLS])
  const [placeholderDropdown, setPlaceholderDropdown] = React.useState<string>('All users')
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const { data: userData } = useUser({
    userId,
  })
  const { data: subperson, loading: subpersonsLoading } = useSubperson({
    userId: userData?.user?.id || '',
  })
  const usersIds = [userId]
  if (subperson) subperson.subperson!.map((subp) => usersIds.push(subp!.id))
  const { data: historyTests, loading: isLoadingHistoryTests } = useQuery<
    HistoryTests,
    HistoryTestsVariables
  >(USER_TEST_HISTORY, {
    variables: {
      filterData: {
        userIds: usersIds,
      },
      pagination: {
        from: 0,
        length: TEST_SESSIONS_PAGE_LENGTH,
        direction: SortDirectionEnum.DESC,
      },
      skipResult: !isSuperAdminOrResults,
    },
  })

  const [testList, setTestList] = React.useState(historyTests?.tests.tests || [])
  const [userTests, setUserTests] = React.useState<HistoryTests_tests_tests[]>()

  function memberIsUser(test: HistoryTests_tests_tests) {
    let returnTest = true
    test.Members.forEach((member: HistoryTests_tests_tests_Members) => {
      if (member.User.parentId && member.isParticipant) {
        returnTest = false
      }
    })
    if (returnTest) return true
    return false
  }

  function subpersonsTests(data: number) {
    return function tests(test: HistoryTests_tests_tests) {
      let returnTest = false
      test.Members.forEach((member: HistoryTests_tests_tests_Members) => {
        if (
          member.User.parentId &&
          member.isParticipant &&
          Number(data) === Number(member.User.id)
        ) {
          setPlaceholderDropdown(`${member?.User?.firstName || ''} ${member?.User?.lastName || ''}`)
          returnTest = true
        }
      })
      if (returnTest) return true
      return false
    }
  }

  React.useEffect(() => {
    if (historyTests) {
      setTestList(historyTests?.tests.tests)
      setUserTests(historyTests?.tests.tests.filter(memberIsUser))
    }
  }, [historyTests])
  const testsDatalistColumns = isSuperAdminOrResults ? 5 : 4
  return (
    <Card borderRadius={`0 0 ${RADIUS.RADIUS_5} ${RADIUS.RADIUS_5}`}>
      <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%" p="1rem">
        {subperson?.subperson && (
          <Dropdown
            name="user"
            value=""
            placeholder={placeholderDropdown}
            options={[
              {
                value: 0,
                text: 'All users',
              },
              {
                value: 1,
                text: `${userData?.user?.firstName || ''} ${userData?.user?.lastName || ''}`,
              },
              ...subperson?.subperson?.map((e) => ({
                value: e?.id || 'No value',
                text: `${e?.firstName || ''} ${e?.lastName || ''}`,
              })),
            ]}
            isLoading={subpersonsLoading}
            isWithOptionsFilter={false}
            selectOnNavigation={false}
            onSelectChange={(
              _: React.SyntheticEvent<HTMLElement>,
              data: { value: number; text: string }
            ) => {
              switch (data.value) {
                case 0:
                  setTestList(historyTests!.tests.tests)
                  setPlaceholderDropdown('All user')
                  break
                case 1:
                  setTestList(userTests!)
                  setPlaceholderDropdown(
                    `${userData?.user?.firstName || ''} ${userData?.user?.lastName || ''}`
                  )
                  break
                default:
                  setTestList(historyTests!.tests.tests.filter(subpersonsTests(data.value)))
              }
            }}
          />
        )}
      </Box>
      <Box
        flex="3"
        minWidth="280px"
        display="grid"
        gridGap="1rem"
        alignContent="start"
        paddingX={15}
        paddingBottom={5}
      >
        <Datalist
          data={testList || []}
          isLoading={isLoadingHistoryTests}
          skeletonProps={{
            columns: SKELETON_COLUMNS,
            rows: TEST_SESSIONS_PAGE_LENGTH,
          }}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={`repeat(${testsDatalistColumns}, 1fr)`}>
              <Datalist.HeaderCell label="Specimen Collected At" />
              <Datalist.HeaderCell label="Patient" />
              <Datalist.HeaderCell label="Tested by" />
              {isSuperAdminOrResults ? <Datalist.HeaderCell label="Result" /> : null}
              <Datalist.HeaderCell label="Barcode" />
            </Datalist.Row>
          )}
          renderTableRow={({
            id,
            testedAt,
            result,
            secondResult,
            barcode,
            Members,
            TestSession,
          }) => {
            const testedBy = Members.filter(({ isStaff }) => isStaff)
              .map(({ User }) => `${User.firstName || ''} ${User.lastName || ''}`)
              .join(', ')
            const isSubperson = Members.filter(({ isParticipant }) => isParticipant).some(
              ({ User }) => User?.parentId
            )
            const userFullName = Members.filter(({ isParticipant }) => isParticipant)
              .map(({ User }) => `${User.firstName || ''} ${User.lastName || ''}`)
              .join(', ')
            return (
              <Datalist.Row
                gridTemplateColumns={`repeat(${testsDatalistColumns}, 1fr)`}
                onClick={() => history.push(`/${ROUTES.TESTS}/${id}`)}
              >
                <Datalist.Cell label="Tested by">
                  {testedAt && getFormattedDateAndTime(testedAt)}
                </Datalist.Cell>
                <Datalist.Cell label="Patient">
                  {isSubperson ? (
                    <Label indicator="green">{userFullName}</Label>
                  ) : (
                    <Label indicator="grayDark">{userFullName}</Label>
                  )}
                </Datalist.Cell>
                <Datalist.Cell label="Tested by">{testedBy}</Datalist.Cell>
                {isSuperAdminOrResults ? (
                  <Datalist.Cell label="Result">
                    {isInfluenza(TestSession.type) && secondResult
                      ? `A: ${result ?? ''} | B: ${secondResult ?? ''}`
                      : result}
                  </Datalist.Cell>
                ) : null}
                <Datalist.Cell label="Barcode">{barcode}</Datalist.Cell>
              </Datalist.Row>
            )
          }}
        />
      </Box>
    </Card>
  )
}
